<template>
    <section class="layout">
        <div class="header-line">
            <div class="profile-label">
                <h3>Follow-Up Sequences</h3>
            </div>

            <div class="seq-multiselect">
                <div class="select-box">
                    <ds-select-field
                        v-model="selectedSequence"
                        placeholder="Select follow up sequence"
                        data-input-label="search-sequence"
                        data-qa="sequence-options"
                        :options="followUpSequences"
                        class="select-dropdown"
                    />
                </div>

                <div class="add-button">
                    <ds-filled-button
                        data-button-label="apply-sequence"
                        :disabled="isSequenceSelected"
                        data-qa="add-button"
                        @click="addToContact"
                    >
                        Add Follow up Sequence
                    </ds-filled-button>
                </div>
            </div>
        </div>

        <div
            v-for="(sequence, index) in followUpSequencesForContact"
            :key="index"
            class="followup-body"
            data-qa="sequence-detail-table"
        >
            <div class="step-title">
                <h4>{{ sequence.campaignName }}</h4>
            </div>

            <div class="table-scroller">
                <table class="sequence-detail">
                    <tr class="sequence-detail-row">
                        <div class="main-detail">
                            <span class="seq-status">Status:</span>
                            <span :style="sequence.status != 'Active' ? {'color' : '#e02500'}: {'color' : 'black'}">{{ sequence.status }}</span>
                        </div>
                    </tr>

                    <tr class="sequence-detail-row">
                        <div class="main-detail">
                            <span class="seq-status">Start date:</span>
                            <span>{{ sequence.startDate }}</span>
                        </div>
                    </tr>

                    <tr v-if="checkStatus(sequence)" class="sequence-detail-row">
                        <div class="main-detail">
                            <span class="seq-status">Date stopped:</span>
                            <span>{{ sequence.inactiveDate }}</span>
                        </div>
                    </tr>

                    <tr>
                        <ds-text-button data-qa="remove-sequence" @click="removeContact(sequence)">
                            Remove
                        </ds-text-button>

                        <ds-text-button v-if="checkStatus(sequence)" data-qa="resume-sequence" @click="resumeContact(sequence)">
                            Resume
                        </ds-text-button>

                        <ds-text-button v-else data-qa="stop-contact" @click="stopContact(sequence)">
                            Stop
                        </ds-text-button>

                        <ds-text-button data-qa="track-response" @click="trackResponse(sequence)">
                            Track Response
                        </ds-text-button>
                    </tr>

                    <div v-if="isCampaignIdValid(sequence)">
                        <table class="sequence-table">
                            <tr>
                                <th class="seq-heading">
                                    Step name
                                </th>

                                <th class="seq-heading">
                                    Scheduled
                                </th>

                                <th class="seq-heading">
                                    Processed
                                </th>

                                <th class="seq-heading">
                                    Status
                                </th>

                                <th class="seq-heading">
                                    Action
                                </th>
                            </tr>

                            <tr
                                v-for="(step, ind) in sequence.sequenceInfoList"
                                :key="ind"
                            >
                                <td class="step-title" :title="`${step.stepName }`">
                                    <span :title="`${step.marketingPieceType}`">
                                        <img :src="`${step.imagePath}`" />
                                    </span>

                                    <a @click="openMailWindow(step)">
                                        {{ step.stepName }}
                                    </a>
                                </td>
                                <td>
                                    {{ step.scheduledDate }}
                                </td>

                                <td>
                                    {{ step.processDate }}
                                </td>

                                <td>
                                    <div class="status-img">
                                        <img :src="`/resources/component/sequence/images/status_${step.processingStatus}.png`" :title="status(step)" />
                                    </div>

                                    <div v-if="isResponseTracked(sequence)" class="status-img">
                                        <img src="/slices/checkmark.png" title="Response tracked" alt="check" />
                                    </div>
                                </td>
                                <td>
                                    <div v-if="checkProcessedDate(step)">
                                        <ds-text-button data-qa="reschedule-seq" @click="rescheduleSequence(step)">
                                            Reschedule
                                        </ds-text-button>
                                    </div>

                                    <div v-else>
                                        <ds-text-button data-qa="run-seq" @click="runSequence(step)">
                                            Run
                                        </ds-text-button>

                                        <ds-text-button class="negative" data-qa="cancel-seq" @click="cancelSequence(step)">
                                            Cancel
                                        </ds-text-button>
                                    </div>
                                </td>
                            </tr>
                        </table>
                    </div>
                </table>
            </div>
        </div>
        <div v-if="!isContactHasSequence" class="no-seq-section" data-qa="no-seq-section">
            <p class="no-seq-label">
                {{ contact.firstName }} does not have any follow-up sequences.
            </p>
        </div>
    </section>
</template>

<script>
import axios from 'axios';

export default {
    props: {
        contact: Object,
        userId: [String, Number],
    },

    data() {
        return {
            url: '/app/campaignChecklist',
            selectedSequence: '',
            followUpSequences: [],
            followUpSequencesForContact: [],
            existingIds: [],
        };
    },

    created() {
        this.getAllSequences();
        this.getAllSequencesForContact();
    },

    computed: {
        isSequenceSelected() {
            return Boolean(!this.selectedSequence);
        },

        isContactHaveSequence() {
            return this.followUpSequencesForContact.length >= 0;
        },

        checkProcessedDate() {
            return (step) => {
                return step.processDate != null;
            };
        },

        checkStatus() {
            return (sequence) => {
                return sequence.completionDate === null && sequence.status === 'Inactive';
            };
        },

        isCampaignIdValid() {
            return (sequence) => {
                return sequence.campaignId != null && sequence.campaignId > 0;
            };
        },

        isResponseTracked() {
            return (sequence) => {
                return sequence.responseInstance === sequence.campaignStepInstanceId;
            };
        },

        isContactHasSequence() {
            return this.followUpSequencesForContact.length > 0;
        },

        status() {
            return (step) => {
                if (step.processingNotes) {
                    const status = step.processingStatus.toUpperCase();

                    return `${status}:${step.processingNotes}`;
                }

                return step.processingStatus.toUpperCase();
            };
        },
    },

    methods: {
        async getAllSequences() {
            const responseData = await axios.get(`${this.url}/sequences`);

            this.followUpSequences = Object.keys(responseData.data).map((key) => {
                return { label: responseData.data[key], value: Number(key) };
            }).sort((prev, next) => prev.label.localeCompare(next.label));
        },

        async getAllSequencesForContact() {
            const responseData = await axios.get(`${this.url}/sequences/${this.contact?.id}?limit=100&offset=0`);

            this.followUpSequencesForContact = responseData?.data;
        },

        addToContact() {
            return this.$confirm({
                message: 'Are you sure you want to add the contact to this Follow-Up Sequence?',
                confirmLabel: 'Yes, Add',
                cancelLabel: 'Cancel',
            }).then(async () => {
                try {
                    const selectedValue = this.followUpSequencesForContact.find((item) => item.campaignId === this.selectedSequence.value);

                    if (selectedValue !== undefined) {
                        this.$toast({ message: 'Contact aleardy added to this sequence' });
                        this.clearSelection();

                        return;
                    }

                    await axios.get(`${this.url}/addToContact/${this.contact.id}?followUpSequenceId=${this.selectedSequence.value}`);

                    this.getAllSequencesForContact();
                    this.$toast({ message: 'Added successfully' });
                    this.clearSelection();
                } catch (error) {
                    this.clearSelection();
                    this.$error({ message: 'Unable to add contact' });
                }
            }).catch(() => {
                this.clearSelection();
            });
        },

        clearSelection() {
            this.selectedSequence = '';
        },

        async removeContact(sequence) {
            await this.$confirm({
                message: 'Are you sure you want to remove the contact from this Follow-Up Sequence? Doing so will delete all history from the contact\'s record. This action cannot be undone!?',
                confirmLabel: 'Yes, Remove',
                cancelLabel: 'Cancel',
            }).then(async () => {
                try {
                    await axios.get(`${this.url}/removeContact/${this.contact.id}?sequenceId=${sequence.campaignId}`);

                    this.getAllSequencesForContact();
                    this.$toast({ message: 'Removed successfully' });
                } catch (error) {
                    this.$error({ message: 'Unable to remove contact' });
                }
            });
        },

        async stopContact(sequence) {
            await this.$confirm({
                message: 'Are you sure you want to stop sending marketing pieces to this contact? You can start the Follow-Up Sequence again later.',
                confirmLabel: 'Yes, Stop',
                cancelLabel: 'Cancel',
            }).then(async () => {
                try {
                    await axios.get(`${this.url}/stopContact/${this.contact.id}?sequenceId=${sequence.campaignId}`);

                    this.getAllSequencesForContact();
                    this.$toast({ message: 'Stopped' });
                } catch (error) {
                    this.$error({ message: 'Unable to stop contact' });
                }
            });
        },

        async resumeContact(sequence) {
            await this.$confirm({
                message: 'Are you sure you want to resume this Follow-Up Sequence?',
                confirmLabel: 'Resume',
                cancelLabel: 'Cancel',
            }).then(async () => {
                try {
                    await axios.get(`${this.url}/resumeContact/${this.contact.id}?sequenceId=${sequence.campaignId}`);

                    this.getAllSequencesForContact();
                    this.$toast({ message: 'Resumed successfully' });
                } catch (error) {
                    this.$error({ message: 'Unable to resume step' });
                }
            });
        },

        async runSequence(sequence) {
            await this.$confirm({
                message: 'Are you sure you want to run this step?',
                confirmLabel: 'Run',
                cancelLabel: 'Cancel',
            }).then(async () => {
                try {
                    await axios.get(`${this.url}/runSequence?pieceId=${sequence.campaignStepInstanceId}`);

                    this.getAllSequencesForContact();
                    this.$toast({ message: 'Run sequence successfully' });
                } catch (error) {
                    this.$error({ message: 'Unable to run sequence for contact' });
                }
            });
        },

        async rescheduleSequence(sequence) {
            await this.$confirm({
                message: 'Are you sure you want to reschedule this step?',
                confirmLabel: 'Reschedule',
                cancelLabel: 'Cancel',
            }).then(async () => {
                try {
                    await axios.get(`${this.url}/rescheduleSequence?pieceId=${sequence.campaignStepInstanceId}`);

                    this.getAllSequencesForContact();
                    this.$toast({ message: 'Rescheduled successfully' });
                } catch (error) {
                    this.$error({ message: 'Unable to reschedule step' });
                }
            });
        },

        async cancelSequence(sequence) {
            await this.$confirm({
                message: 'Are you sure you want to cancel this step?',
                confirmLabel: 'Ok',
                cancelLabel: 'Cancel',
            }).then(async () => {
                try {
                    await axios.get(`${this.url}/cancelSequence?pieceId=${sequence.campaignStepInstanceId}`);

                    this.getAllSequencesForContact();
                    this.$toast({ message: 'Canceled successfully' });
                } catch (error) {
                    this.$error({ message: 'Unable to cancel step.' });
                }
            });
        },

        trackResponse(sequence) {
            window.open(`/Campaign/respond.jsp?start=true&campaigneeId=${sequence.campaigneeId}`);
        },

        openMailWindow(step) {
            window.open(`/SystemTemplate/manageTemplate.jsp?templateStyle=pop&view=edit&ID=${step.campaignPieceId}&sequenceId=${step.campaignId}`);
        },
    },
};
</script>

<style scoped lang="scss">
.header-line {
    padding: $spacing-300 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.seq-status {
  width: 15%;
}

.no-seq-section {
    width: 100%;
    text-align: center;
    border-top: 1px solid $color-gray-lighter;
    border-top:1px solid $color-gray-lighter;
}

.no-seq-label {
    color: $color-text;
    font-size: $font-size-md;
    font-weight: $font-weight-semibold;
    padding-top: $spacing-800;
    padding-bottom: $spacing-600;
    line-height: normal;
}

.seq-multiselect {
    --input-margin-bottom: 0;
    display: flex;
}

.seq-heading {
    font-weight: $font-weight-bold;
}

.seq-data {
    text-align: center;
}

.add-button {
    padding-left: $spacing-100;
}

.select-box {
    width: px-to-rem(300px);
    height: auto;
}

.followup-body {
    margin-top: $spacing-200;
}

.headings {
    font-weight: $font-weight-bold;
}

.sequence-table > tr:first-child {
    border-bottom: 0;
}

.email-icon {
    --icon-size: 0.9rem;
}

.th {
    font-weight: $font-weight-bold;
}

.step-title {
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.profile-label {
    display: flex;
    align-items: center;

    h2 {
        margin-right: $spacing-100;
    }
}
.table-scroller {
    overflow: auto;
    white-space: nowrap;
}

.sequence-detail {
    margin-top: $spacing-200;
    margin-bottom: $spacing-200;
    border:none;
}

.sequence-detail-row {
    border:none;
}

.status-img {
    padding-left: $spacing-200;
}

.main-detail {
    display: flex;
    margin-top: 0.5 * $spacing-200;
}

.multiselect-dropdown {
    --dropdown-height: #{px-to-rem(310px)};
}

@media only screen and ($medium) {
    .header-line {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .seq-multiselect {
        display: flex;
    }

    .add-button {
        padding-left: $spacing-100;
    }
}

@media only screen and ($small) {

    .header-line, .seq-multiselect {
        display: block;
    }

    .select-box {
        margin: $spacing-200 0;
    }

    .add-button {
        padding-left: 0px;
    }
}
</style>
